import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, Settings, AlertTriangle, WifiOff } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../hooks/useToast';
import { useTenantFormat } from '../../hooks/useTenantFormat';
import { fmtRelative, fmtDateTime } from '../../lib/format';
import { cbApi, resolveSyncError, normalizeSummary, readLastGood, writeLastGood } from '../../lib/connectedBusinesses';
import { ErpSection, ShowroomsSection, MfgSection } from './BakemillSections';

const PILL = {
  synced: { label: 'Synced', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500' },
  cached: { label: 'Cached', cls: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'bg-amber-500' },
  failed: { label: 'Sync failed', cls: 'bg-red-50 text-red-700 border-red-200', dot: 'bg-red-500' },
};

function StatusPill({ status }) {
  const p = PILL[status];
  if (!p) return null;
  return (
    <span data-testid="bakemill-status-pill" data-status={status} className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[11px] font-bold ${p.cls}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${p.dot}`} />{p.label}
    </span>
  );
}

function SyncedAt({ iso }) {
  return (
    <span className="text-xs text-gray-500" title={iso ? fmtDateTime(iso) : undefined} data-testid="bakemill-last-synced">
      {iso ? `Last synced: ${fmtRelative(iso)}` : 'Not yet synced'}
    </span>
  );
}

function useBakemillSummary() {
  const { token, tenant } = useAuth();
  const toast = useToast();
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastGood, setLastGood] = useState(() => readLastGood(tenant?.id));

  const load = useCallback(async (refresh) => {
    if (!token) return;
    if (refresh) setRefreshing(true);
    try {
      const res = await cbApi.summary(token, refresh);
      setResult(res); setError(null);
      writeLastGood(tenant?.id, res);
      setLastGood({ data: res.data, last_synced_at: res.last_synced_at });
      if (refresh) toast.show('Bakemill ERP synced', 'success');
    } catch (e) {
      const info = await resolveSyncError(token, e);
      setError(info);
      if (refresh) toast.show(`Bakemill ERP sync failed: ${info.detail}`, 'error');
    } finally { setLoading(false); setRefreshing(false); }
  }, [token, tenant?.id, toast]);

  useEffect(() => { load(false); }, [load]);
  return { result, error, loading, refreshing, lastGood, load };
}

export default function BakemillCard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { formatMoney } = useTenantFormat();
  const isOwner = String(user?.role || '').toLowerCase() === 'owner';
  const { result, error, loading, refreshing, lastGood, load } = useBakemillSummary();
  const [, setTick] = useState(0);
  useEffect(() => { const id = setInterval(() => setTick(t => t + 1), 30000); return () => clearInterval(id); }, []);

  const status = loading ? null : error ? 'failed' : result?.cached ? 'cached' : 'synced';
  const syncedAt = error ? lastGood?.last_synced_at : result?.last_synced_at;
  const shownData = error ? lastGood?.data : result?.data;

  return (
    <div data-testid="bakemill-erp-card" data-state={status || 'loading'}
      className="glass-card rounded-3xl p-5 lg:p-6 bg-white relative overflow-hidden">
      {error && <div aria-hidden="true" data-testid="bakemill-error-strip" className="absolute inset-x-0 top-0 h-1 bg-red-500" />}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <h3 className="text-lg font-extrabold text-gray-900" data-testid="bakemill-card-title">Bakemill ERP</h3>
        <StatusPill status={status} />
        <SyncedAt iso={syncedAt} />
        <div className="ml-auto flex items-center gap-2">
          <button type="button" onClick={() => load(true)} disabled={refreshing} data-testid="bakemill-refresh-btn"
            className="flex items-center gap-1.5 h-9 px-3 rounded-xl border border-gray-200 bg-white text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400">
            <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} /> Refresh
          </button>
          {isOwner && (
            <button type="button" onClick={() => navigate('/connected-businesses/settings')} aria-label="Connected Businesses settings" data-testid="bakemill-settings-btn"
              className="h-9 w-9 flex items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-500 hover:text-gray-900 hover:bg-gray-50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400">
              <Settings size={16} />
            </button>
          )}
        </div>
      </div>
      <CardBody loading={loading} error={error} data={shownData} money={formatMoney} isOwner={isOwner} onRetry={() => load(true)} onSettings={() => navigate('/connected-businesses/settings')} />
    </div>
  );
}

function CardBody({ loading, error, data, money, isOwner, onRetry, onSettings }) {
  if (loading) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="bakemill-loading">
        {[0, 1, 2, 3].map(i => <div key={i} className="h-[74px] rounded-xl bg-gray-100 animate-pulse" />)}
      </div>
    );
  }
  if (error && !data) {
    if (error.unreachable) {
      return (
        <div className="flex items-center gap-3 rounded-xl bg-red-50 border border-red-100 p-4" data-testid="bakemill-unreachable">
          <WifiOff size={18} className="text-red-500 shrink-0" />
          <p className="text-sm text-red-700 font-medium flex-1">Cannot reach integration server. <span className="text-red-500/80 font-normal">({error.detail})</span></p>
          <button type="button" onClick={onRetry} data-testid="bakemill-retry-btn" className="h-9 px-3 rounded-xl bg-red-500 text-white text-sm font-bold hover:bg-red-600 transition-colors">Retry</button>
        </div>
      );
    }
    return (
      <div className="rounded-xl bg-amber-50 border border-amber-100 p-4" data-testid="bakemill-not-configured">
        <p className="text-sm font-semibold text-amber-800">
          Not configured yet — {isOwner
            ? <button type="button" onClick={onSettings} className="underline font-bold hover:text-amber-900" data-testid="bakemill-goto-settings">go to Settings</button>
            : 'ask the business owner to configure it'}
        </p>
        <p className="text-xs text-amber-700/80 mt-1">Bakemill ERP sync failed: {error.detail}</p>
      </div>
    );
  }
  const n = normalizeSummary(data);
  return (
    <div className="space-y-5">
      {error && (
        <div className="flex items-start gap-2 rounded-xl bg-red-50 border border-red-100 px-4 py-3" data-testid="bakemill-error-banner">
          <AlertTriangle size={16} className="text-red-500 mt-0.5 shrink-0" />
          <p className="text-sm text-red-700 font-medium">Bakemill ERP sync failed: {error.detail} <span className="text-red-500/80 font-normal">— showing data from the last successful sync.</span></p>
        </div>
      )}
      <ErpSection erp={n.erp} money={money} />
      <ShowroomsSection showrooms={n.showrooms} money={money} />
      <MfgSection mfg={n.mfg} />
    </div>
  );
}
